/* ─── KuraCo · Cart burst ───────────────────────────────────────────────
   On a successful add-to-cart, spawn a small flock of vegetable
   silhouettes at the click origin and animate them toward the mini-cart
   icon in the header. Gives a tactile, on-brand confirmation that the
   item went somewhere — no Matter.js, no canvas.

   Wire-up:
     • <script defer src="{{ 'kuraco-cart-burst.js' | asset_url }}"></script>
     • Listens to window event 'kuraco:cart:add-success' which kuraco.js
       emits via kcEvents bus (mirrored to a CustomEvent on window).
     • Falls back to capturing pointerdown on .kc-card__add / .kc-pdp__add
       so we know the burst origin even if the cart event fires later.

   Constraints:
     • prefers-reduced-motion: silently skip
     • <kuraco-cart-popover> custom element handles the drawer; we just
       throw confetti, we don't open it.
   ──────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Compact, recognisable shapes — long stalks (cinnamon, snake gourd) skipped
  // because they don't read at this size.
  var SHAPES = [
    'illustration-tomato.svg',
    'illustration-garlic-clove.svg',
    'illustration-pea.svg',
    'illustration-carrot-chopped.svg',
    'illustration-chana-split-dal.svg',
    'illustration-cashew.svg',
    'illustration-mung-dal.svg',
    'illustration-bayleaf.svg',
    'illustration-chilli.svg',
    'illustration-onion-chopped.svg'
  ];

  // theme.liquid sets window.KuracoIlloBase via a one-line liquid filter so
  // we can construct asset URLs without a CDN-root parser. The trailing /
  // (or query-string format) is preserved in the value.
  var SHAPE_URL = (function () {
    var map = {};
    var base = window.KuracoIlloBase || '';
    if (!base) {
      var p = document.querySelector('kuraco-particles[data-illo-base]');
      if (p) base = p.getAttribute('data-illo-base') || '';
    }
    for (var i = 0; i < SHAPES.length; i++) {
      map[SHAPES[i]] = base + SHAPES[i];
    }
    return map;
  })();

  var DEFAULT_PALETTE = ['#b74a00', '#2bcc91', '#f98d43', '#cff784', '#2a2c33'];

  function currentPalette() {
    // Prefer the hero's active scheme so the burst harmonises with the
    // current page colours.
    var hero = document.querySelector('kuraco-hero-physics[data-palette]');
    if (hero) {
      var hex = (hero.dataset.palette || '').trim().split(/\s+/).filter(Boolean);
      if (hex.length) return hex;
    }
    return DEFAULT_PALETTE;
  }

  function findCartIconCenter() {
    var btn = document.querySelector('[data-cart-trigger]');
    if (!btn) return null;
    var r = btn.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2, el: btn };
  }

  // Burst fires on click directly — NOT on cart:add-success — because the
  // Shopify add.js POST takes 1–2 s and we want instant visual feedback.
  // If the network add fails we accept that the burst played anyway; it's
  // a celebratory animation, not a state-of-truth indicator.
  function findAddButton(target) {
    if (!(target instanceof Element)) return null;
    return target.closest(
      // Add-to-cart variants + the quantity-increment buttons (+) on the
      // card, PDP, mini-cart, and main-cart line rows. Each + click is
      // effectively another add-to-cart from the user's POV.
      '.kc-card__add, .kc-pdp__add, [name="add"], button[type="submit"][form*="product"], [data-inc], [data-qty-inc]'
    );
  }

  // ---------------------------------------------------------------------
  // Burst piece
  // ---------------------------------------------------------------------

  // ---------------------------------------------------------------------
  // Irregular polygon (matches hero "dust" particles) — clip-path version
  //   Cheaper than SVG masks: just a coloured div with a CSS polygon clip.
  //   Vertices come from generateIrregularPolygon, normalised to 0..100 %
  //   so the polygon scales with the piece's box size.
  // ---------------------------------------------------------------------

  function generateParticleClipPath() {
    var n = 5 + Math.floor(Math.random() * 4); // 5..8 sides
    var step = (Math.PI * 2) / n;
    var pts = [];
    for (var i = 0; i < n; i++) {
      var angle = step * i + (Math.random() - 0.5) * step * 0.6;
      var r = 0.5 * (0.5 + Math.random() * 0.5); // half-extent in 0..0.5 of box
      var px = 50 + Math.cos(angle) * r * 100;   // %
      var py = 50 + Math.sin(angle) * r * 100;
      pts.push(px.toFixed(1) + '% ' + py.toFixed(1) + '%');
    }
    return 'polygon(' + pts.join(',') + ')';
  }

  function spawnPiece(originX, originY, target, palette, onArrive, opts) {
    opts = opts || {};
    var isParticle = opts.kind === 'particle';
    var piece = document.createElement('span');
    piece.className = 'kc-cart-burst-piece';
    var color = palette[Math.floor(Math.random() * palette.length)];
    var size = isParticle
      ? 7 + Math.random() * 9   // 7..16 px — small dust
      : 18 + Math.random() * 16; // 18..34 px — recognisable vegetable

    var styling;
    if (isParticle) {
      var clip = generateParticleClipPath();
      styling =
        'background-color:' + color + ';' +
        'clip-path:' + clip + ';' +
        '-webkit-clip-path:' + clip + ';';
    } else {
      var shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
      var url = SHAPE_URL[shape] || shape;
      styling =
        'background-color:' + color + ';' +
        '-webkit-mask:url(' + url + ') no-repeat center / contain;' +
        'mask:url(' + url + ') no-repeat center / contain;';
    }

    piece.style.cssText =
      'position:fixed;' +
      'left:0;top:0;' +
      'width:' + size + 'px;height:' + size + 'px;' +
      'transform:translate3d(' + originX + 'px,' + originY + 'px,0);' +
      styling +
      'pointer-events:none;' +
      'will-change:transform,opacity;' +
      'z-index:2147483646;';
    document.body.appendChild(piece);

    // Cubic Bézier path from origin → target. One continuous curve replaces
    // the old "ballistic then lerp" handoff that caused a visible jolt at
    // the phase boundary.
    //
    //   P0 = origin
    //   P1 = "burst out" point — pushed away from the origin in a random
    //        direction biased toward the target line (gives the puff feel)
    //   P2 = anchor near the target so the curve approaches it head-on
    //        rather than sliding past
    //   P3 = target (cart icon centre)
    //
    // Per-piece randomness on P1's angle and distance keeps the flock
    // looking like a flock; ease-in-out timing makes the motion feel
    // deliberate at both ends.

    var dxT = target.x - originX;
    var dyT = target.y - originY;
    var distT = Math.sqrt(dxT * dxT + dyT * dyT) || 1;
    var baseAngle = Math.atan2(dyT, dxT);

    var spread = (Math.random() - 0.5) * (Math.PI * 0.7); // ±63°
    var burstAngle = baseAngle + spread - Math.PI * 0.12; // slight upward bias
    var burstDist = distT * (0.35 + Math.random() * 0.35);
    var c1x = originX + Math.cos(burstAngle) * burstDist;
    var c1y = originY + Math.sin(burstAngle) * burstDist;

    // Pull P2 a short way back along the origin-target line so the curve
    // tangent at the cart icon points roughly inward.
    var c2x = target.x - dxT * 0.18;
    var c2y = target.y - dyT * 0.18;

    var rot = (Math.random() - 0.5) * 360;
    var rotV = (Math.random() - 0.5) * 720; // deg/s
    var startTime = performance.now();
    var duration = 700 + Math.random() * 200;
    var arrivedFired = false;

    function easeInOutCubic(t) {
      return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function bezier(p0, p1, p2, p3, t) {
      var u = 1 - t;
      return u * u * u * p0
           + 3 * u * u * t * p1
           + 3 * u * t * t * p2
           + t * t * t * p3;
    }

    function frame(now) {
      var elapsed = (now - startTime) / 1000;
      var progress = Math.min(1, elapsed * 1000 / duration);
      var t = easeInOutCubic(progress);

      var x = bezier(originX, c1x, c2x, target.x, t);
      var y = bezier(originY, c1y, c2y, target.y, t);
      rot += rotV * (1 / 60);

      // Compress into the icon during the last 30 % — scale shrinks toward
      // ~25 % size so the piece visually "absorbs" rather than vanishing
      // mid-flight. transform-origin defaults to 50 % 50 %.
      var scale = 1;
      var fade = 1;
      if (progress > 0.7) {
        var s = (progress - 0.7) / 0.3;
        scale = 1 - s * 0.75;
        fade = 1 - s * 0.55;
      }

      piece.style.transform =
        'translate3d(' + x + 'px,' + y + 'px,0) ' +
        'rotate(' + rot + 'deg) scale(' + scale + ')';
      piece.style.opacity = String(fade);

      if (!arrivedFired && progress > 0.8) {
        arrivedFired = true;
        pulseCartIcon();
        if (typeof onArrive === 'function') onArrive();
      }

      if (progress >= 1) {
        piece.remove();
        return;
      }
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  // ---------------------------------------------------------------------
  // Optimistic cart count bump
  //   Increment the visible count the moment a burst piece arrives so the
  //   user gets immediate feedback while the actual add.js POST is still
  //   in flight. kuraco.js's cart:updated handler will overwrite with the
  //   server-truth count once the response lands — if the bump was wrong
  //   (network failure, sold-out, etc.) the count snaps back. Worst case
  //   = a 1.5 s tick of "1 → 2 → 1" which is gentler than no feedback.
  // ---------------------------------------------------------------------

  function bumpCartCount() {
    var els = document.querySelectorAll('[data-cart-count]');
    els.forEach(function (el) {
      var text = (el.textContent || '0').trim();
      var current = parseInt(text, 10);
      if (!Number.isFinite(current)) current = 0;
      el.textContent = String(current + 1);
      el.dataset.empty = 'false';
    });
  }

  // ---------------------------------------------------------------------
  // Cart icon pulse
  // ---------------------------------------------------------------------

  var pulseTimeout = null;
  function pulseCartIcon() {
    var btn = document.querySelector('[data-cart-trigger]');
    if (!btn) return;
    btn.classList.add('is-burst-impact');
    if (pulseTimeout) clearTimeout(pulseTimeout);
    pulseTimeout = window.setTimeout(function () {
      btn.classList.remove('is-burst-impact');
      pulseTimeout = null;
    }, 320);
  }

  // ---------------------------------------------------------------------
  // Burst orchestrator
  // ---------------------------------------------------------------------

  function burstFromOrigin(originX, originY) {
    var target = findCartIconCenter();
    if (!target) return; // no header cart icon on this page; bail
    var palette = currentPalette();
    var vegCount = 7 + Math.floor(Math.random() * 3);      // 7..9 vegetables
    var dustCount = 6 + Math.floor(Math.random() * 4);     // 6..9 dust particles
    var bumped = false;
    function onFirstArrival() {
      if (bumped) return;
      bumped = true;
      bumpCartCount();
    }
    // Vegetables — fire first so they "lead" visually.
    for (var i = 0; i < vegCount; i++) {
      (function (idx) {
        setTimeout(function () {
          spawnPiece(originX, originY, target, palette, onFirstArrival);
        }, idx * 18);
      })(i);
    }
    // Dust — overlapping stagger so they sprinkle alongside, not after.
    for (var j = 0; j < dustCount; j++) {
      (function (idx) {
        setTimeout(function () {
          spawnPiece(originX, originY, target, palette, onFirstArrival, { kind: 'particle' });
        }, idx * 22);
      })(j);
    }
  }

  // ---------------------------------------------------------------------
  // Trigger
  // ---------------------------------------------------------------------

  // Burst on click of any add-to-cart button — fires immediately so the
  // user gets feedback within ~16 ms instead of waiting for the add.js
  // POST (~1.5 s on Shopify). If the add itself fails, the brief animation
  // is a small visual lie that's strictly better than 1.5 s of nothing.
  // Suppression rule: skip when the button is already disabled (which the
  // add handler in kuraco.js sets briefly during in-flight requests).
  document.addEventListener('click', function (e) {
    var btn = findAddButton(e.target);
    if (!btn || btn.disabled) return;
    var rect = btn.getBoundingClientRect();
    var ox = (e.clientX || rect.left + rect.width / 2);
    var oy = (e.clientY || rect.top + rect.height / 2);
    burstFromOrigin(ox, oy);
  }, { capture: true, passive: true });

  // Manual entry point for callers that want to burst from JS code.
  window.kuracoCartBurst = function (x, y) {
    if (typeof x !== 'number' || typeof y !== 'number') {
      x = window.innerWidth / 2;
      y = window.innerHeight / 2;
    }
    burstFromOrigin(x, y);
  };
})();
